// apps/console/src/components/organisms/AlertInbox/alert-log.ts
//
// The pure model behind the alert LOG: which alerts are shown, and how they become rows.
//
// The log has two sources for one alert. The live store holds only OPEN alerts and is fast; the
// fetch (`GET /robots/alerts?status=all`) is the only place a handled alert — acknowledged or
// resolved — can come from, and the only source of the stored `sightings` array (the live frame
// never carries it). They are merged by alertId, and each alert becomes ONE row (its newest
// occurrence, with a count): a camera repeats the same alarm every few seconds. Expanding an alert
// unrolls it into one row per occurrence, each with its own photo, for "when" as a list.
//
// An occurrence carries its own photo when the alarm came with one (a camera-AI alarm names the
// picture it was raised for). The pairing is exact: the server stores each photo with the same time
// it stores for that occurrence. A picture an alarm names is shown on the alarm's row and not again
// as a "Picture taken" row of its own.
//
// Two things this deliberately does NOT do, because the data does not support them:
//   - place the unlisted occurrences in time: the server keeps the newest 20 per alert and a true
//     count, so the shortfall is stated as a gap row, never interpolated (see `occurrenceTimes`);
//   - give each occurrence its own location: only the presence's LATEST fix is stored.
// A picture no alarm names (Take photo, a manual capture) still interleaves by time, nothing more.

import type { RobotMediaItem } from '../../../services/api/domains/robots.api'
import { overlayDefined } from '../../../services/api/robot-alerts-merge'
import type { RobotAlertItem } from '../../../services/api/robot-alerts-types'
import { countUnlisted, occurrenceTimes } from './alert-display'
import { normalizeSeverity, type AlertSeverity } from './alert-severity'

/** Rows rendered at most. Past this the footer says so; 500 `<li>`s with lazy images is fine. */
export const MAX_LOG_ROWS = 500

/**
 * Mirror of the robots service's `MAX_ALERTS` and `MAX_MEDIA`. `services/` is not importable from
 * the app, so the number lives here too — a fetch that comes back with exactly this many rows is
 * almost certainly truncated, and the footer says so rather than pretending the list is complete.
 */
export const SERVER_LIST_CAP = 200

export type AlertLogScope = { kind: 'fleet' } | { kind: 'robot'; robotId: string }

export interface AlarmEntry {
  kind: 'alarm'
  key: string
  at: string
  atMs: number
  alert: RobotAlertItem
  /** The most recent occurrence — the one row that carries Enroll. */
  isNewest: boolean
  /** The storage key of the photo taken for THIS occurrence, or null when it came without one. */
  photoRef: string | null
  /** That photo as a fetched media row, when there is one, so the row can show its thumbnail. */
  photo: RobotMediaItem | null
  /** On the newest row of an alert that happened more than once; null on every other row. */
  repeats: AlarmRepeats | null
}

export interface AlarmRepeats {
  /** Every occurrence, including the ones the server no longer lists. */
  count: number
  /** The first occurrence: when the alarm started. */
  firstAt: string
  /** Whether the older occurrences are listed as rows of their own. */
  expanded: boolean
}

export interface PictureEntry {
  kind: 'picture'
  key: string
  at: string
  atMs: number
  media: RobotMediaItem
}

/** "N earlier occurrences not listed" — sits directly under the oldest listed occurrence. */
export interface GapEntry {
  kind: 'gap'
  key: string
  at: string
  atMs: number
  alert: RobotAlertItem
  count: number
}

export type LogEntry = AlarmEntry | PictureEntry | GapEntry

export interface AlertLogModel {
  entries: LogEntry[]
  /** Rows before the render cap, so the footer can say "showing the newest N of total". */
  total: number
  openCount: number
  alarmCount: number
  pictureCount: number
}

/**
 * Fold the live store into the fetched list, by alertId.
 *
 * Fetched-only rows pass through — that is how acknowledged and resolved alerts enter the log.
 * Store-only rows pass through — an alert pushed live after the fetch began. When both hold an
 * alert, the fetched row is the base and the store's DEFINED keys win: the store is fresher on
 * `lastSeenAt` / `sightingCount` / `name`, while `sightings` (which no live frame carries) survives
 * from the fetch. `overlayDefined` is what makes an undefined key on the live side keep the base.
 */
export function mergeAlertLog(
  fetched: readonly RobotAlertItem[],
  store: readonly RobotAlertItem[],
  seen: readonly RobotAlertItem[] = [],
): RobotAlertItem[] {
  const byId = new Map<string, RobotAlertItem>()

  for (const a of fetched) byId.set(a.alertId, a)
  for (const a of store) byId.set(a.alertId, overlayDefined(byId.get(a.alertId), a))
  // `seen` is the per-mount memory of alerts the store once held. It exists only so an alert that
  // was acked elsewhere does not flicker out for the ~1.5 s until the refetch returns it with its
  // new status — so it fills GAPS and never overrides: a fetched row (fresh status) or a live row
  // (fresh everything) always wins over the cached copy, whose `status` is stale by definition.
  for (const a of seen) if (!byId.has(a.alertId)) byId.set(a.alertId, a)

  return [...byId.values()]
}

/**
 * Scope + severity filter, both sides normalised — a saved `warning` reads as `high`, an unknown
 * value as `info`, so a filter never silently drops the alarms it was meant to select.
 */
export function filterAlerts(
  alerts: readonly RobotAlertItem[],
  scope: AlertLogScope,
  severityFilter?: ReadonlyArray<AlertSeverity | 'warning'>,
): RobotAlertItem[] {
  const wanted = severityFilter && severityFilter.length > 0 ? new Set(severityFilter.map(normalizeSeverity)) : null

  return alerts.filter(
    a =>
      (scope.kind === 'fleet' || a.robotId === scope.robotId) &&
      (wanted === null || wanted.has(normalizeSeverity(a.severity))),
  )
}

/** Both coordinates present and finite — the only shape "show on map" may act on. */
export function hasCoords(x: { lng?: number; lat?: number }): x is { lng: number; lat: number } {
  return Number.isFinite(x.lng) && Number.isFinite(x.lat)
}

/** A picture the console can actually show: an image that has landed in storage under a key. */
export function isStoredPicture(m: RobotMediaItem): boolean {
  return m.kind === 'image' && m.status === 'stored' && typeof m.key === 'string' && m.key !== ''
}

/**
 * The photo taken for one occurrence of an alert, or null.
 *
 * Its own photo, matched by the exact time the server stored it with. Failing that, the newest
 * occurrence may use `snapshotRef`, the alert's latest photo, because a live repeat reaches the
 * console before the refetch that lists it. But not when an earlier occurrence already owns that
 * photo: a repeat that came without a picture leaves the previous one in `snapshotRef`.
 */
export function occurrencePhoto(alert: RobotAlertItem, atMs: number, isNewest: boolean): string | null {
  const own = alert.sightingSnapshots?.find(p => Date.parse(p.at) === atMs)

  if (own) return own.ref
  if (!isNewest || !alert.snapshotRef) return null

  return alert.sightingSnapshots?.some(p => p.ref === alert.snapshotRef) ? null : alert.snapshotRef
}

const KIND_RANK: Record<LogEntry['kind'], number> = { alarm: 0, picture: 1, gap: 2 }

/**
 * Newest first. Unparseable times sink to the bottom rather than throwing the order; equal times
 * keep the alarm above a picture and a gap under its own alarm's oldest row; the key breaks the
 * last tie so the order is deterministic across re-renders and refetches.
 */
function compareEntries(a: LogEntry, b: LogEntry): number {
  const aOk = Number.isFinite(a.atMs)
  const bOk = Number.isFinite(b.atMs)

  if (aOk !== bOk) return aOk ? -1 : 1
  if (aOk && a.atMs !== b.atMs) return b.atMs - a.atMs
  if (KIND_RANK[a.kind] !== KIND_RANK[b.kind]) return KIND_RANK[a.kind] - KIND_RANK[b.kind]

  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
}

const NONE_EXPANDED: ReadonlySet<string> = new Set()

/** When an alert started: its first occurrence, or the oldest one listed when that is unreadable. */
function firstOccurrence(alert: RobotAlertItem, times: readonly string[]): string {
  return alert.recordedAt && Number.isFinite(Date.parse(alert.recordedAt))
    ? alert.recordedAt
    : (times[times.length - 1] as string)
}

/**
 * Alerts and pictures as one chronological list. An alert is one row unless its id is in `expanded`;
 * a collapsed alert still claims its hidden occurrences' photos, so they get no "Picture taken" rows.
 */
export function buildLogEntries(
  alerts: readonly RobotAlertItem[],
  media: readonly RobotMediaItem[],
  { maxRows = MAX_LOG_ROWS, expanded = NONE_EXPANDED }: { maxRows?: number; expanded?: ReadonlySet<string> } = {},
): AlertLogModel {
  const entries: LogEntry[] = []
  const pictures = media.filter(isStoredPicture)
  const pictureByKey = new Map(pictures.map(m => [m.key as string, m]))
  // Pictures shown on an alarm's row, which therefore get no row of their own.
  const onAlarmRows = new Set<string>()

  const alarmEntry = (
    alert: RobotAlertItem,
    key: string,
    at: string,
    atMs: number,
    isNewest: boolean,
    repeats: AlarmRepeats | null,
  ): AlarmEntry => {
    const photoRef = occurrencePhoto(alert, atMs, isNewest)

    if (photoRef) onAlarmRows.add(photoRef)

    return {
      kind: 'alarm' as const,
      key,
      at,
      atMs,
      alert,
      isNewest,
      photoRef,
      photo: photoRef ? (pictureByKey.get(photoRef) ?? null) : null,
      repeats,
    }
  }

  for (const alert of alerts) {
    const times = occurrenceTimes(alert)

    // An alert with no usable time at all still deserves a row — it sinks to the bottom, but a
    // recorded alarm that vanishes from a log is worse than one at the wrong end of it.
    if (times.length === 0) {
      entries.push(alarmEntry(alert, `${alert.alertId}:?`, '', Number.NaN, true, null))
      continue
    }

    const open = expanded.has(alert.alertId)
    const total = Math.max(alert.sightingCount ?? 1, times.length)
    const repeats = total > 1 ? { count: total, firstAt: firstOccurrence(alert, times), expanded: open } : null

    times.forEach((at, i) => {
      const atMs = Date.parse(at)

      if (i > 0 && !open) {
        // Not a row of its own, but its photo still belongs to this alarm.
        const photoRef = occurrencePhoto(alert, atMs, false)

        if (photoRef) onAlarmRows.add(photoRef)

        return
      }

      const key = `${alert.alertId}:${Number.isFinite(atMs) ? atMs : at}`

      entries.push(alarmEntry(alert, key, at, atMs, i === 0, i === 0 ? repeats : null))
    })

    if (!open) continue

    const count = countUnlisted(alert, times.length)
    const oldest = times[times.length - 1] as string

    if (count > 0)
      entries.push({ kind: 'gap', key: `${alert.alertId}:gap`, at: oldest, atMs: Date.parse(oldest), alert, count })
  }

  for (const m of pictures) {
    if (onAlarmRows.has(m.key as string)) continue
    entries.push({ kind: 'picture', key: `media:${m.id}`, at: m.capturedAt, atMs: Date.parse(m.capturedAt), media: m })
  }

  entries.sort(compareEntries)

  return {
    entries: entries.slice(0, maxRows),
    total: entries.length,
    openCount: alerts.filter(a => a.status === 'open').length,
    alarmCount: alerts.length,
    // Every stored picture is in the log, on its own row or on its alarm's.
    pictureCount: pictures.length,
  }
}

/** "3 Sep" — the day beside a clock time, because a log spans days and a bare HH:MM:SS lies. */
export function formatLogDay(iso: string, locale?: string): string {
  const ms = Date.parse(iso)

  if (!Number.isFinite(ms)) return ''

  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(ms))
}
